import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Clock, DollarSign, Plane } from 'lucide-react';
import { format } from 'date-fns';

const AvailableRides = () => {
  const { user } = useContext(AuthContext);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailableRides();
  }, []);

  const fetchAvailableRides = async () => {
    try {
      const response = await axios.get(`${API}/rides/available/list`);
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to load available rides');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/claim`);
      toast.success('Ride claimed successfully!');
      fetchAvailableRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim ride');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading available rides...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="available-rides-page">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Available Rides</h2>
        <p className="text-gray-600 mt-1">Claim rides that need a driver</p>
      </div>

      {rides.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center py-8 text-gray-500">No available rides at the moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {rides.map((ride) => (
            <Card key={ride.id} className="hover:shadow-md transition-shadow" data-testid={`available-ride-${ride.id}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold">{ride.client_name}</h3>
                        <p className="text-sm text-gray-600">{ride.client_phone}</p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Available
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{format(new Date(ride.date), 'EEE, MMM dd, yyyy - h:mm a')}</span>
                      </div>
                      
                      <div className="flex items-start gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <strong>Pickup:</strong> {ride.pickup_location}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs"
                            >
                              (map)
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <strong>Dropoff:</strong> {ride.dropoff_location}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs"
                            >
                              (map)
                            </a>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-4 text-gray-600">
                        <span>{ride.miles_driven} miles</span>
                        <span>•</span>
                        <span>{ride.time_hours} hrs</span>
                      </div>
                      
                      {ride.is_airport_pickup && ride.flight_number && (
                        <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg">
                          <Plane className="w-4 h-4" />
                          <span className="font-medium">Airport Pickup</span>
                          <span>•</span>
                          <span>Flight: {ride.flight_number}</span>
                          <a
                            href={`https://www.flightaware.com/live/flight/${ride.flight_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs ml-2"
                          >
                            (track flight)
                          </a>
                        </div>
                      )}
                    </div>

                    {ride.notes && (
                      <div className="text-sm text-gray-500 italic">
                        Note: {ride.notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end justify-between gap-3 min-w-[140px]">
                    <div className="text-right space-y-1">
                      <div className="text-sm text-gray-600">You'll Earn</div>
                      <div className="text-2xl font-bold text-green-600 flex items-center gap-1">
                        <DollarSign className="w-5 h-5" />
                        {ride.net_earnings.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">after commission</div>
                    </div>
                    
                    <Button
                      onClick={() => handleClaim(ride.id)}
                      className="w-full"
                      data-testid={`claim-ride-${ride.id}`}
                    >
                      Claim Ride
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvailableRides;
